import React, { useState } from 'react';
import { Card, Row, Col, Badge, Table, ProgressBar, Form, Button } from 'react-bootstrap';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import { 
  FaChartLine, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaFileAlt,
  FaClock,
  FaUserTie,
  FaTachometerAlt,
  FaDownload,
  FaCalendarAlt
} from 'react-icons/fa';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface ComplianceMetrics {
  overallScore: number;
  documentsProcessed: number;
  averageProcessingTime: number;
  criticalIssues: number;
  autoApprovalRate: number;
  agentPerformance: Array<{
    name: string;
    complianceRate: number;
    documentsSubmitted: number;
    averageScore: number;
  }>;
}

const ComplianceDashboard: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const [selectedMetric, setSelectedMetric] = useState<'compliance' | 'volume' | 'time'>('compliance');

  // Mock metrics data
  const metrics: ComplianceMetrics = {
    overallScore: 94.5,
    documentsProcessed: 1847,
    averageProcessingTime: 3.2,
    criticalIssues: 23,
    autoApprovalRate: 78.5,
    agentPerformance: [
      { name: 'Sarah Johnson', complianceRate: 98.2, documentsSubmitted: 245, averageScore: 96.5 },
      { name: 'Mike Davis', complianceRate: 95.8, documentsSubmitted: 189, averageScore: 94.2 },
      { name: 'Lisa Chen', complianceRate: 93.5, documentsSubmitted: 312, averageScore: 92.8 },
      { name: 'Tom Wilson', complianceRate: 91.2, documentsSubmitted: 178, averageScore: 90.5 },
      { name: 'Emma Brown', complianceRate: 89.7, documentsSubmitted: 156, averageScore: 88.9 }
    ]
  };

  // Chart data
  const complianceTrendData = {
    labels: timeRange === 'week' ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] :
            timeRange === 'month' ? ['Week 1', 'Week 2', 'Week 3', 'Week 4'] :
            timeRange === 'quarter' ? ['Month 1', 'Month 2', 'Month 3'] :
            ['Q1', 'Q2', 'Q3', 'Q4'],
    datasets: [
      {
        label: 'Compliance Score',
        data: timeRange === 'week' ? [92, 93, 94, 91, 95, 96, 94] :
              timeRange === 'month' ? [93, 94, 92, 95] :
              timeRange === 'quarter' ? [91, 93, 94] :
              [90, 92, 93, 94],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1
      },
      {
        label: 'Auto-Approval Rate',
        data: timeRange === 'week' ? [75, 77, 78, 74, 80, 82, 79] :
              timeRange === 'month' ? [76, 78, 77, 80] :
              timeRange === 'quarter' ? [75, 77, 79] :
              [74, 76, 78, 79],
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        tension: 0.1
      }
    ]
  };

  const documentTypeData = {
    labels: ['Purchase Agreement', 'Listing Agreement', 'Disclosure Forms', 'Addendums', 'Other'],
    datasets: [{
      data: [45, 25, 15, 10, 5],
      backgroundColor: [
        'rgba(255, 99, 132, 0.8)',
        'rgba(54, 162, 235, 0.8)',
        'rgba(255, 206, 86, 0.8)',
        'rgba(75, 192, 192, 0.8)',
        'rgba(153, 102, 255, 0.8)'
      ]
    }]
  };

  const issueDistributionData = {
    labels: ['Missing Signatures', 'Date Issues', 'Blank Fields', 'Wrong Version', 'Other'],
    datasets: [{
      label: 'Issue Count',
      data: [45, 32, 28, 15, 12],
      backgroundColor: 'rgba(255, 99, 132, 0.8)'
    }]
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Compliance Trends'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100
      }
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 95) return 'success';
    if (score >= 90) return 'warning';
    return 'danger';
  };

  return (
    <div>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h4>
                <FaTachometerAlt className="me-2" />
                Compliance Analytics Dashboard
              </h4>
              <p className="text-muted mb-0">
                Real-time compliance metrics and performance analytics
              </p>
            </div>
            <div>
              <Button variant="outline-primary" size="sm" className="me-2">
                <FaDownload className="me-2" />
                Export Report
              </Button>
              <Form.Select 
                size="sm" 
                style={{ width: 'auto', display: 'inline-block' }}
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value as any)}
              >
                <option value="week">Last Week</option>
                <option value="month">Last Month</option>
                <option value="quarter">Last Quarter</option>
                <option value="year">Last Year</option>
              </Form.Select>
            </div>
          </div>
        </Col>
      </Row>

      {/* Key Metrics Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">Overall Compliance</h6>
                  <h3 className={`text-${getScoreColor(metrics.overallScore)}`}>
                    {metrics.overallScore}%
                  </h3>
                </div>
                <div className={`text-${getScoreColor(metrics.overallScore)}`}>
                  <FaChartLine size={32} />
                </div>
              </div>
              <ProgressBar 
                now={metrics.overallScore} 
                variant={getScoreColor(metrics.overallScore)}
                className="mt-2"
                style={{ height: '5px' }}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">Documents Processed</h6>
                  <h3 className="text-primary">{metrics.documentsProcessed.toLocaleString()}</h3>
                </div>
                <div className="text-primary">
                  <FaFileAlt size={32} />
                </div>
              </div>
              <small className="text-success">
                <FaCheckCircle className="me-1" />
                +12% from last period
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">Avg Processing Time</h6>
                  <h3 className="text-info">{metrics.averageProcessingTime} min</h3>
                </div>
                <div className="text-info">
                  <FaClock size={32} />
                </div>
              </div>
              <small className="text-success">
                -18% faster than average
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">Critical Issues</h6>
                  <h3 className="text-danger">{metrics.criticalIssues}</h3>
                </div>
                <div className="text-danger">
                  <FaExclamationTriangle size={32} />
                </div>
              </div>
              <small className="text-danger">
                Requires immediate attention
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts Row */}
      <Row className="mb-4">
        <Col md={8}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Compliance Trends</h5>
            </Card.Header>
            <Card.Body>
              <Line data={complianceTrendData} options={chartOptions} />
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Document Types</h5>
            </Card.Header>
            <Card.Body>
              <Doughnut data={documentTypeData} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Agent Performance Table */}
      <Row className="mb-4">
        <Col md={8}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <FaUserTie className="me-2" />
                Agent Performance Rankings
              </h5>
            </Card.Header>
            <Card.Body>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Agent Name</th>
                    <th>Compliance Rate</th>
                    <th>Documents</th>
                    <th>Avg Score</th>
                    <th>Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.agentPerformance.map((agent, idx) => (
                    <tr key={idx}>
                      <td>
                        <Badge bg={idx < 3 ? 'success' : 'secondary'}>
                          #{idx + 1}
                        </Badge>
                      </td>
                      <td>
                        <strong>{agent.name}</strong>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <ProgressBar 
                            now={agent.complianceRate} 
                            variant={getScoreColor(agent.complianceRate)}
                            style={{ width: '100px', height: '10px' }}
                            className="me-2"
                          />
                          <span>{agent.complianceRate}%</span>
                        </div>
                      </td>
                      <td>{agent.documentsSubmitted}</td>
                      <td>
                        <Badge bg={getScoreColor(agent.averageScore)}>
                          {agent.averageScore}
                        </Badge>
                      </td>
                      <td>
                        <span className={agent.complianceRate > 92 ? 'text-success' : 'text-danger'}>
                          {agent.complianceRate > 92 ? '↑' : '↓'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Common Issues</h5>
            </Card.Header>
            <Card.Body>
              <Bar 
                data={issueDistributionData} 
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'top' as const,
                    },
                    title: {
                      display: false
                    }
                  }
                }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Quick Stats */}
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <h5 className="mb-3">Quick Stats</h5>
              <Row>
                <Col md={3}>
                  <div className="text-center">
                    <h6 className="text-muted">Auto-Approval Rate</h6>
                    <h4 className="text-success">{metrics.autoApprovalRate}%</h4>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <h6 className="text-muted">First-Time Pass Rate</h6>
                    <h4 className="text-info">86.2%</h4>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <h6 className="text-muted">Avg Resolution Time</h6>
                    <h4 className="text-warning">4.5 hrs</h4>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <h6 className="text-muted">Compliance Violations</h6>
                    <h4 className="text-danger">3</h4>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ComplianceDashboard;